import React, { useEffect, useRef, useState } from "react";
import { useLoader } from "../../context/Loader/LoaderProvider";
import Loader from "../../context/Loader/Loader";
import { showErrorToast } from "../../utils/Toaster";
import jsPDF from "jspdf";
import "jspdf-autotable"; // Import autoTable plugin
import { TabView, TabPanel } from "primereact/tabview";
import { Calendar } from "primereact/calendar"; // Import Calendar component
import * as XLSX from "xlsx";
import axiosPrivate from "../../hooks/axiosPrivate";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FaCircleInfo, FaRegFilePdf } from "react-icons/fa6";
import { FaRegFileExcel } from "react-icons/fa";
import { FcClearFilters } from "react-icons/fc";
import { Tooltip } from "primereact/tooltip";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";

// Tab index constants (enum-like object)
const TAB_INDICES = {
  MONTHLY_ACTUAL_DISPATCH: 0,
  MONTHLY_PROJECTION_DISPATCH: 1,
  STAGE_WISE_AMOUNT: 2,
};

const Reports = () => {
  const userData = JSON.parse(localStorage.getItem("userData")) || "{}";
  const { isLoading, setIsLoading } = useLoader();
  const [jobsData, setJobsData] = useState([]);
  const [stageAmountData, setStageAmountData] = useState([]);
  const [projectionDispatchData, setProjectionDispatchData] = useState([]);
  const [isComponentReady, setIsComponentReady] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(
    TAB_INDICES.MONTHLY_ACTUAL_DISPATCH,
  );
  const [customDateSelceted, setCustomDateSelceted] = useState(false);
  const [selectedPlant, setSelectedPlant] = useState(0);
  const [selectedGroup, setSelectedGroup] = useState(0);
  const [selectedLdClause, setSelectedLdClause] = useState("");
  const [productGroupsData, setProductGroupsData] = useState([]);
  const [totalSumActual, setTotalSumActual] = useState();
  const [totalSumProjectionOrder, setTotalSumProjectionOrder] = useState();
  const [totalSumProjectionPending, setTotalSumProjectionPending] = useState();
  const [totalSumRevisedPending, setTotalSumRevisedPending] = useState();
  const [filterBy, setFilterBy] = useState("order_date");
  const [globalSearchKeyword, setGlobalSearchKeyword] = useState("");
  const dt = useRef(null);
  const [projectionFirst, setProjectionFirst] = useState(0);
  const [projectionRows, setProjectionRows] = useState(80);
  const [excelDropdownOpen, setExcelDropdownOpen] = useState(false);

  const fetchJobsData = async (action) => {
    try {
      setIsLoading(true);
      let response;
      if (fromDate && toDate) {
        const options = { day: "2-digit", month: "2-digit", year: "2-digit" };
        response = await axiosPrivate.post("reports/monthly_dispatch", {
          fromDate:
            action === "clear"
              ? null
              : new Date(fromDate).toLocaleDateString("en-GB", options),
          toDate:
            action === "clear"
              ? null
              : new Date(toDate).toLocaleDateString("en-GB", options),
          group_id: Number(selectedGroup) || 0,
          plant_code: Number(selectedPlant) || 0,
        });
      } else {
        response = await axiosPrivate.post("reports/monthly_dispatch", {
          group_id: Number(selectedGroup) || 0,
          plant_code: Number(selectedPlant) || 0,
        });
      }

      if (response.status === 200) {
        const Data = response?.data?.data || [];
        setProductGroupsData(response?.data?.prod_groups || []);
        setJobsData([...Data]);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        showErrorToast(error?.message);
      } else if (error.response?.status === 404) {
        setJobsData([]);
        setProductGroupsData(error?.response?.data?.prod_groups || []);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStageAmountData = async (action) => {
    try {
      setIsLoading(true);
      let response;
      // If backend supports plant filter for this endpoint, send plant_id
      response = await axiosPrivate.post("reports/stage_wise_amount", {
        plant_code: Number(selectedPlant) || 0,
      });
      if (response.status === 200) {
        setStageAmountData(response?.data?.data || []);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        showErrorToast(error?.message);
      } else if (error.response?.status === 404) {
        setStageAmountData([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProjectionDispatchData = async (action) => {
    try {
      setIsLoading(true);
      let response;
      if (fromDate && toDate) {
        const options = {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
        };
        response = await axiosPrivate.post("reports/projection_dispatch", {
          fromDate:
            action === "clear"
              ? null
              : new Date(fromDate).toLocaleDateString("en-GB", options),
          toDate:
            action === "clear"
              ? null
              : new Date(toDate).toLocaleDateString("en-GB", options),
          group_id: Number(selectedGroup) || 0,
          filter_by: filterBy,
          plant_code: Number(selectedPlant) || 0,
          ld_clause: selectedLdClause,
        });
      } else {
        response = await axiosPrivate.post("/reports/projection_dispatch", {
          group_id: Number(selectedGroup) || 0,
          plant_code: Number(selectedPlant) || 0,
          ld_clause: selectedLdClause,
        });
      }

      if (response.status === 200) {
        setProjectionDispatchData(
          response?.data?.data?.map((item, index) => ({
            ...item,
            tooltipTarget: `tooltip-${index}`,
          })) || [],
        );
        setProductGroupsData(response?.data?.prod_groups || []);
        // setTotalSumProjection(response?.data?.total_sum);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        showErrorToast(error?.message);
      } else if (error.response?.status === 404) {
        setProductGroupsData(error?.response?.data?.prod_groups || []);
        setProjectionDispatchData([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    switch (activeTabIndex) {
      case TAB_INDICES.MONTHLY_ACTUAL_DISPATCH:
        fetchJobsData();
        break;
      case TAB_INDICES.MONTHLY_PROJECTION_DISPATCH:
        fetchProjectionDispatchData();
        break;
      case TAB_INDICES.STAGE_WISE_AMOUNT:
        fetchStageAmountData();
        break;
      default:
        break;
    }
  }, [activeTabIndex, selectedGroup, selectedPlant, selectedLdClause]);

  useEffect(() => {
    setIsComponentReady(true);
  }, []);

  useEffect(() => {
    // Clear search when tab changes
    setGlobalSearchKeyword("");
  }, [activeTabIndex]);

  // Close excel dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setExcelDropdownOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Auto-apply date filters when both dates are selected
  useEffect(() => {
    if (fromDate && toDate) {
      switch (activeTabIndex) {
        case TAB_INDICES.MONTHLY_ACTUAL_DISPATCH:
          fetchJobsData();
          break;
        case TAB_INDICES.STAGE_WISE_AMOUNT:
          fetchStageAmountData();
          break;
        case TAB_INDICES.MONTHLY_PROJECTION_DISPATCH:
          fetchProjectionDispatchData();
          break;
        default:
          break;
      }
    }
  }, [fromDate, toDate, activeTabIndex]);

  const getActiveData = () => {
    if (activeTabIndex === TAB_INDICES.MONTHLY_ACTUAL_DISPATCH) {
      return jobsData;
    } else if (activeTabIndex === TAB_INDICES.STAGE_WISE_AMOUNT) {
      return stageAmountData;
    } else if (activeTabIndex === TAB_INDICES.MONTHLY_PROJECTION_DISPATCH) {
      return projectionDispatchData;
    }
    return [];
  };

  const getFilteredData = () => {
    let activeData = getActiveData();

    // Apply plant filtering for projection dispatch data (tab index 1)
    if (
      activeTabIndex === TAB_INDICES.MONTHLY_PROJECTION_DISPATCH &&
      Number(selectedPlant) !== 0
    ) {
      activeData = activeData?.filter(
        (item) => Number(item.Plant) === Number(selectedPlant),
      );
    }

    if (!globalSearchKeyword.trim()) {
      return activeData;
    }

    const keyword = globalSearchKeyword.toLowerCase().trim();

    return activeData.filter((item) => {
      // Search across multiple fields based on active tab
      if (activeTabIndex === TAB_INDICES.MONTHLY_ACTUAL_DISPATCH) {
        // Dispatch Report fields
        return (
          item.ProdGroups?.toLowerCase().includes(keyword) ||
          item.OrderNum?.toString().toLowerCase().includes(keyword) ||
          item.CustomerName?.toLowerCase().includes(keyword) ||
          item.State?.toLowerCase().includes(keyword) ||
          item.ProdQty?.toString().toLowerCase().includes(keyword) ||
          item.IndianAmt?.toString().toLowerCase().includes(keyword)
        );
      } else if (activeTabIndex === TAB_INDICES.STAGE_WISE_AMOUNT) {
        // Stage-wise Amount Report fields
        return (
          item.stage_name?.toLowerCase().includes(keyword) ||
          item.jobs_count?.toString().toLowerCase().includes(keyword) ||
          item.total_IndianAmt?.toString().toLowerCase().includes(keyword)
        );
      } else if (activeTabIndex === TAB_INDICES.MONTHLY_PROJECTION_DISPATCH) {
        // Monthly Projection Dispatch Report fields
        return (
          item.OrderNum?.toString().toLowerCase().includes(keyword) ||
          item.CustomerName?.toLowerCase().includes(keyword) ||
          item.PendingQty?.toString().toLowerCase().includes(keyword) ||
          item.PendingAmt?.toString().toLowerCase().includes(keyword) ||
          item.remarks?.toLowerCase().includes(keyword)
        );
      }
      return false;
    });
  };
  const exportPdf = () => {
    if (!isComponentReady || !dt.current) return;

    const doc = new jsPDF();

    const activeData = getFilteredData();

    const generateHeader = (currentPage, pageSize) => {
      doc.setFontSize(12); // Increased font size for the heading
      doc.setTextColor(40);
      doc.setFont("helvetica", "normal");
      if (currentPage > 1) {
        doc.setPage(currentPage);
      }
      doc.text(
        activeTabIndex === TAB_INDICES.MONTHLY_ACTUAL_DISPATCH
          ? "Tempsens - Dispatch Report"
          : activeTabIndex === TAB_INDICES.STAGE_WISE_AMOUNT
          ? "Tempsens - Stage-wise Amount Report"
          : "Tempsens - Monthly Projection Dispatch Report",
        pageSize.width / 2,
        17,
        { align: "center" },
      );
      doc.setFontSize(10);
      if (
        fromDate &&
        toDate &&
        (activeTabIndex === TAB_INDICES.MONTHLY_ACTUAL_DISPATCH ||
          activeTabIndex === TAB_INDICES.MONTHLY_PROJECTION_DISPATCH)
      ) {
        doc.text(
          `From: ${fromDate.toLocaleDateString(
            "en-GB",
          )} to ${toDate.toLocaleDateString("en-GB")}`,
          10,
          22,
        );
      }

      if (
        Number(selectedPlant) !== 0 &&
        (activeTabIndex === TAB_INDICES.MONTHLY_ACTUAL_DISPATCH ||
          activeTabIndex === TAB_INDICES.MONTHLY_PROJECTION_DISPATCH)
      ) {
        doc.text(
          `Plant: ${
            userData?.company_and_plants?.find(
              (plant) => Number(plant.plant_code) === Number(selectedPlant),
            )?.plant_name
          }`,
          10,
          22,
        );
      }

      doc.text(
        `Total ${
          activeTabIndex === TAB_INDICES.MONTHLY_ACTUAL_DISPATCH ||
          activeTabIndex === TAB_INDICES.MONTHLY_PROJECTION_DISPATCH
            ? "Jobs"
            : "Stages"
        }: ${activeData.length}`,
        pageSize.width - 10,
        22,
        {
          align: "right",
        },
      );

      if (
        activeTabIndex === TAB_INDICES.MONTHLY_ACTUAL_DISPATCH &&
        totalSumActual
        // || (activeTabIndex === TAB_INDICES.MONTHLY_PROJECTION_DISPATCH && totalSumProjectionOrder)
      ) {
        const totalSum =
          activeTabIndex === TAB_INDICES.MONTHLY_ACTUAL_DISPATCH
            ? totalSumActual
            : totalSumProjectionOrder;
        doc.text(`Order Amount: ${totalSum}`, pageSize.width - 10, 27, {
          align: "right",
        });
      }

      if (
        activeTabIndex === TAB_INDICES.MONTHLY_PROJECTION_DISPATCH &&
        totalSumProjectionPending
      ) {
        const totalSum = totalSumProjectionPending;
        doc.text(`Pending Order: ${totalSum}`, 10, 27, {
          align: "left",
        });
      }

      doc.text(
        `Page ${currentPage}`,
        pageSize.width / 2,
        pageSize.height - 10,
        {
          align: "center",
        },
      );
      doc.setFontSize(12);
    };

    const columns =
      activeTabIndex === TAB_INDICES.MONTHLY_ACTUAL_DISPATCH
        ? [
            { title: "Sr. No.", dataKey: "index" },
            { title: "Product Groups", dataKey: "ProdGroups" },
            { title: "Order No.", dataKey: "OrderNum" },
            { title: "Order Date", dataKey: "OrderDt" },
            { title: "Customer Name", dataKey: "CustomerName" },
            { title: "State", dataKey: "State" },
            { title: "Production Quantity", dataKey: "ProdQty" },
            { title: "Total Amount", dataKey: "IndianAmt" },
            { title: "Due Date", dataKey: "target_date" },
            { title: "Release Date", dataKey: "release_date" },
          ]
        : activeTabIndex === TAB_INDICES.STAGE_WISE_AMOUNT
        ? [
            { title: "Sr. No.", dataKey: "index" },
            { title: "Stage Name", dataKey: "stage_name" },
            { title: "Stage Load", dataKey: "jobs_count" },
            { title: "Total Amount", dataKey: "total_IndianAmt" },
          ]
        : [
            { title: "Sr. No.", dataKey: "index" },
            // { title: "Product Groups", dataKey: "ProdGroups" },
            { title: "Order No.", dataKey: "OrderNum" },
            { title: "Order Date", dataKey: "OrderDt" },
            { title: "Customer Name", dataKey: "CustomerName" },
            // { title: "State", dataKey: "State" },
            // { title: "Order Quantity", dataKey: "OrderQty" },
            // { title: "Order Amount", dataKey: "IndianAmt" },
            { title: "Pending Quantity", dataKey: "PendingQty" },
            { title: "Pending Amount", dataKey: "PendingAmt" },
            // { title: "Order Type", dataKey: "OrderType" },
            // { title: "Line Cancel", dataKey: "SOCancel" },
            { title: "Due Date", dataKey: "DueDt" },
            { title: "Promise Date", dataKey: "target_date" },
            { title: "Remarks", dataKey: "remarks" },
            // { title: "Release Date", dataKey: "release_date" },
          ];

    const rows = activeData.map((row, index) => ({
      ...row,
      index: index + 1,
      IndianAmt: row.IndianAmt
        ? Number(row.IndianAmt).toLocaleString("en-IN")
        : "-",
      PendingAmt: row.PendingAmt
        ? Number(row.PendingAmt).toLocaleString("en-IN")
        : "-",
      ProdQty: row.ProdQty ? row.ProdQty : "-",
      release_date: row.release_date
        ? new Date(row.release_date).toLocaleDateString("en-GB")
        : "-",
      DueDt: row.DueDt ? new Date(row.DueDt).toLocaleDateString("en-GB") : "-",
      target_date: row.target_date
        ? new Date(row.target_date).toLocaleDateString("en-GB")
        : "-",
      OrderDt: row.OrderDt
        ? new Date(row.OrderDt).toLocaleDateString("en-GB")
        : "-",
      State: row.State ? row.State : "-",
      OrderType: row.OrderType ? row.OrderType : "-",
      OrderNum: row.OrderNum ? row.OrderNum : "-",
      ProdGroups: row.ProdGroups ? row.ProdGroups : "-",
      SOCancel: row.SOCancel ? row.SOCancel : "-",
      LDClause: row.LDClause ? row.LDClause : "-",
    }));

    let finalY = 30; // Starting Y position for the table

    doc.autoTable({
      head: [columns.map((column) => column.title)],
      body: rows.map((row) => columns.map((column) => row[column.dataKey])),
      startY: finalY,
      theme: "grid",
      margin: { top: 30 }, // Ensures there's enough space for the header
      styles: {
        overflow: "linebreak", // Ensure text doesn't overflow cells
        fontSize: 6,
      },
      headStyles: {
        fillColor: [35, 16, 74],
        textColor: [255, 255, 255], // White text color for contrast
        fontStyle: "bold", // Bold text for the header
      },
      columnStyles: {
        // Use the dataKey, not the title!
        PendingAmt: { halign: "right" },
        PendingQty: { halign: "right" },
      },
      bodyStyles: {
        valign: "top",
      },
      rowPageBreak: "avoid", // Avoid splitting rows between pages
      willDrawCell: function (data) {
        if (data.row.section === "body") {
          const remainingSpace = doc.internal.pageSize.height - data.cursor.y;
          const rowHeight = data.cell.height;
          if (rowHeight > remainingSpace) {
            finalY = 30; // Reset Y position for the new page
            doc.addPage();
            generateHeader(
              doc.internal.getNumberOfPages(),
              doc.internal.pageSize,
            );
          }
        }
      },
      didDrawPage: function (data) {
        generateHeader(data.pageNumber, doc.internal.pageSize);
      },
    });

    doc.save(
      activeTabIndex === TAB_INDICES.MONTHLY_ACTUAL_DISPATCH
        ? "dispatch_report.pdf"
        : activeTabIndex === TAB_INDICES.STAGE_WISE_AMOUNT
        ? "stagewise_amount_report.pdf"
        : "projection_dispatch_report.pdf",
    );
  };

  const exportExcel = () => {
    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";
    const fileName =
      activeTabIndex === TAB_INDICES.MONTHLY_ACTUAL_DISPATCH
        ? "dispatch_data"
        : activeTabIndex === TAB_INDICES.STAGE_WISE_AMOUNT
        ? "stagewise_amount_data"
        : "projection_dispatch_report";

    const activeData = getFilteredData()?.map((item, index) => {
      // Destructure to remove 'id' and 'tooltipTarget'
      const { id, tooltipTarget, DueDt, target_date, revisedPendingAmt, ...rest } = item;
      return {
        ...rest,
        OrderDt: item.OrderDt
          ? new Date(item.OrderDt).toLocaleDateString("en-GB")
          : "-",
        DueDate: DueDt ? new Date(DueDt).toLocaleDateString("en-GB") : "-",
        PromiseDate: target_date
          ? new Date(target_date).toLocaleDateString("en-GB")
          : "-",
        revisedPendingAmt: (revisedPendingAmt !== undefined && revisedPendingAmt !== null && revisedPendingAmt !== "")
          ? revisedPendingAmt
          : item.PendingAmt,
      };
    });
    const ws = XLSX.utils.json_to_sheet(activeData);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: fileType });
    const url = window.URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName + fileExtension;
    a.click();
  };


  const exportExcelProjectionSummary = async (type) => {
    try {
      setIsLoading(true);
      const options = {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      };
      const payload = {
        group_id: Number(selectedGroup) || 0,
        plant_code: Number(selectedPlant) || 0,
        type: type,
      };
      if (fromDate && toDate) {
        payload.fromDate = new Date(fromDate).toLocaleDateString("en-GB", options);
        payload.toDate = new Date(toDate).toLocaleDateString("en-GB", options);
      }
      const response = await axiosPrivate.post("/reports/monthly_projection_dispatch_summary", payload, {
        responseType: "blob",
      });

      const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      const fileExtension = ".xlsx";
      const fileName = type === "revised" ? "revised_projection_summary_report" : "pending_projection_summary_report";

      const blob = new Blob([response.data], { type: fileType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName + fileExtension;
      a.click();
    } catch (error) {
      showErrorToast(error?.message || "Failed to download projection summary.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearFilter = () => {
    setFromDate(null);
    setToDate(null);
    setSelectedGroup(0);
    setSelectedPlant(0);
    setGlobalSearchKeyword("");
    setCustomDateSelceted(false);
    setSelectedLdClause("");

    // reset projection table paginator to first page on clear
    setProjectionFirst(0);

    // Fetch data with cleared filters
    switch (activeTabIndex) {
      case TAB_INDICES.MONTHLY_ACTUAL_DISPATCH:
        fetchJobsData("clear");
        break;
      case TAB_INDICES.STAGE_WISE_AMOUNT:
        fetchStageAmountData();
        break;
      case TAB_INDICES.MONTHLY_PROJECTION_DISPATCH:
        fetchProjectionDispatchData("clear");
        break;
      default:
        break;
    }
  };

  const renderHeader = () => {
    return (
      <div
        className="row d-flex justify-content-between align-items-center pl-2 pr-2 "
        id="reports"
      >
        <div>
          <div>
            <>
              <button className="btn btn-primary" onClick={exportPdf}>
                <FaRegFilePdf size={17} className="mr-1" />
                Export as PDF
              </button>
              <button className="btn btn-primary ml-2" onClick={exportExcel}>
                <FaRegFileExcel size={17} className="mr-1" />
                Export as Excel
              </button>
            </>
            {activeTabIndex === TAB_INDICES.MONTHLY_PROJECTION_DISPATCH && (
              <div className="btn-group ml-2" style={{ position: "relative" }}>
                <button
                  className="btn btn-primary dropdown-toggle"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExcelDropdownOpen(!excelDropdownOpen);
                  }}
                >
                  <FaRegFileExcel size={17} className="mr-1" />
                  Summary Report
                </button>
                {excelDropdownOpen && (
                  <div
                    className="dropdown-menu show"
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      zIndex: 1000,
                      display: "block",
                    }}
                  >
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        setExcelDropdownOpen(false);
                        exportExcelProjectionSummary("revised");
                      }}
                    >
                      Revised Amount
                    </button>
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        setExcelDropdownOpen(false);
                        exportExcelProjectionSummary("pending");
                      }}
                    >
                      Pending Amount
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <p
            className="mt-2"
            style={{
              fontSize: "16px",
            }}
          >
            {getFilteredData().length} Total Entries
          </p>
        </div>

        <div>
          <div className="d-flex justify-content-between align-items-center row">
            <div className="mb-2 d-flex justify-content-between align-items-center">
              {/* Global Search Input */}
              <div className="m-2">
                <label htmlFor="globalSearch">Search:</label>
                <div className="d-flex flex-column col-md text-left w-100 pl-0">
                  <InputText
                    id="globalSearch"
                    value={globalSearchKeyword}
                    onChange={(e) => setGlobalSearchKeyword(e.target.value)}
                    placeholder="Search across all columns..."
                    className="form-control"
                    style={{ minWidth: "200px", height: "30px" }}
                  />
                </div>
              </div>
              {Number(userData?.role_id) === 3 &&
                (activeTabIndex === TAB_INDICES.MONTHLY_ACTUAL_DISPATCH ||
                  activeTabIndex ===
                    TAB_INDICES.MONTHLY_PROJECTION_DISPATCH) && (
                  <div className="m-2">
                    <label htmlFor="plant_code">Plant:</label>
                    <div className="d-flex flex-column col-md dropdown text-left w-100 pl-0">
                      <select
                        className="btn dropdown-toggle text-left"
                        style={{ background: "#fff", border: "1px solid #ced4da", borderRadius: "4px", height: "33px", padding: "0 8px" }}
                        onChange={(e) => {
                          // if (Number(e.target.value) !== 2010) {
                          // }
                          setSelectedGroup(0);
                          setSelectedPlant(e.target.value);
                        }}
                        value={selectedPlant}
                      >
                        <option value="0">All</option>
                        {userData?.company_and_plants?.map((plant) => (
                          <option
                            value={plant?.plant_code}
                            key={plant?.plant_id}
                          >
                            {plant?.plant_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              {/* {(Number(selectedPlant) === 2010 ||
                (Number(userData?.role_id) === 1 &&
                  Number(userData?.company_code) === 2000)) && ( */}
              <div className="m-2">
                <label htmlFor="product_group">Product Groups:</label>
                <div className=" d-flex flex-column col-md dropdown text-left w-100 pl-0">
                  <select
                    className="btn dropdown-toggle text-left"
                    style={{ background: "#fff", border: "1px solid #ced4da", borderRadius: "4px", height: "33px", padding: "0 8px" }}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    value={selectedGroup}
                  >
                    <option value="0">All</option>
                    {productGroupsData?.map((group) => (
                      <option value={group?.id} key={group?.id}>
                        {group?.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              {activeTabIndex === TAB_INDICES.MONTHLY_PROJECTION_DISPATCH && (
                <div className="m-2">
                  <label htmlFor="ld_clause">LD Clause:</label>
                  <div className=" d-flex flex-column col-md dropdown text-left w-100 pl-0">
                    <select
                      className="btn dropdown-toggle text-left"
                      style={{ background: "#fff", border: "1px solid #ced4da", borderRadius: "4px", height: "33px", padding: "0 8px" }}
                      onChange={(e) => setSelectedLdClause(e.target.value)}
                      value={selectedLdClause}
                    >
                      <option value="">All</option>
                      <option value="YES">YES</option>
                      <option value="NO">NO</option>
                    </select>
                  </div>
                </div>
              )}
              {/* )} */}
              {/* Filter By Dropdown */}
              {activeTabIndex === TAB_INDICES.MONTHLY_PROJECTION_DISPATCH ? (
                <div className="m-2">
                  <label htmlFor="filter_by">Filter By:</label>
                  <div className="d-flex flex-column col-md dropdown text-left w-100 pl-0">
                    <select
                      className="btn dropdown-toggle text-left"
                      style={{ background: "#fff", border: "1px solid #ced4da", borderRadius: "4px", height: "33px", padding: "0 8px" }}
                      id="filter_by"
                      value={filterBy}
                      onChange={(e) => setFilterBy(e.target.value)}
                    >
                      <option value="order_date">Order Date</option>
                      <option value="promise_date">Promise Date</option>
                    </select>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Calendar inputs for custom range */}
            <div className="d-flex flex-column col-md">
              <span>From: </span>
              <Calendar
                value={fromDate}
                onChange={(e) => setFromDate(e.value)}
                showIcon
                readOnlyInput
                dateFormat="dd-mm-yy"
                placeholder="dd-mm-yyyy"
              />
              {!fromDate && customDateSelceted ? (
                <div
                  style={{
                    color: "red",
                    fontSize: "12px",
                  }}
                >
                  Please select From Date!
                </div>
              ) : null}
            </div>
            <div className="d-flex flex-column  col-md">
              <span className="ml-2">To: </span>
              <Calendar
                value={toDate}
                onChange={(e) => setToDate(e.value)}
                readOnlyInput
                showIcon
                dateFormat="dd-mm-yy"
                placeholder="dd-mm-yyyy"
              />
              {!toDate && customDateSelceted && (
                <div
                  style={{
                    color: "red",
                    fontSize: "12px",
                  }}
                >
                  Please select to Date!
                </div>
              )}
            </div>
            <div className="col-auto">
              <FcClearFilters
                className=" ml-2 mt-3"
                size={25}
                onClick={handleClearFilter}
                style={{ cursor: "pointer" }}
                title="Clear all filters"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const header = renderHeader();

  const indexBodyTemplate = (rowData) => {
    return <span>{rowData.index}</span>;
  };

  const renderProductCategory = (rowData) => {
    const text = rowData.ProdGroups || "-";
    const displayText = text.length > 30 ? text.substring(0, 30) + "..." : text;
    return (
      <>
        <span id={`prodGroupTooltip_${rowData.OrderNum}`}>{displayText}</span>
        {text.length > 30 && (
          <Tooltip
            target={`#prodGroupTooltip_${rowData.OrderNum}`}
            content={text}
          />
        )}
      </>
    );
  };

  useEffect(() => {
    const calculateTotalSum = (data, field) => {
      return data
        ?.filter((item) =>
          Number(selectedPlant) === 0
            ? true
            : Number(item.Plant) === Number(selectedPlant),
        )
        ?.reduce((acc, item) => {
          let value = item[field] || 0;
          if (typeof value === "string") {
            value = value.replace(/,/g, "");
          }
          value = Number(value);
          return acc + (isNaN(value) ? 0 : value);
        }, 0)
        .toFixed(2);
    };

    const calculateRevisedPendingSum = (data) => {
      return data
        ?.filter((item) =>
          Number(selectedPlant) === 0
            ? true
            : Number(item.Plant) === Number(selectedPlant),
        )
        ?.reduce((acc, item) => {
          let value = (item.revisedPendingAmt !== undefined && item.revisedPendingAmt !== null && item.revisedPendingAmt !== "")
            ? item.revisedPendingAmt
            : (item.PendingAmt || 0);
          if (typeof value === "string") {
            value = value.replace(/,/g, "");
          }
          value = Number(value);
          return acc + (isNaN(value) ? 0 : value);
        }, 0)
        .toFixed(2);
    };

    if (activeTabIndex === TAB_INDICES.MONTHLY_ACTUAL_DISPATCH) {
      setTotalSumActual(calculateTotalSum(jobsData, "IndianAmt"));
    } else if (activeTabIndex === TAB_INDICES.MONTHLY_PROJECTION_DISPATCH) {
      setTotalSumProjectionOrder(
        calculateTotalSum(projectionDispatchData, "IndianAmt"),
      );
      setTotalSumProjectionPending(
        calculateTotalSum(projectionDispatchData, "PendingAmt"),
      );
      setTotalSumRevisedPending(
        calculateRevisedPendingSum(projectionDispatchData),
      );
    }
  }, [selectedPlant, activeTabIndex, jobsData, projectionDispatchData]);

  const handleRemarksUpdate = async (
    new_remarks,
    OrderNum,
    revisedPendingAmt,
  ) => {
    try {
      const response = await axiosPrivate.post(
        "reports/add_remarks_for_pending_report",
        {
          remarks: new_remarks,
          ordernum: OrderNum,
          revisedPendingAmt: revisedPendingAmt,
        },
      );

      if (response.status === 200) {
        fetchProjectionDispatchData();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdateTargetDate = async (date, jobOrderNum) => {
    const options = { day: "2-digit", month: "2-digit", year: "2-digit" };
    try {
      const response = await axiosPrivate.put(
        `job/target_date_job/${jobOrderNum}`,
        {
          date: new Date(date).toLocaleDateString("en-GB", options),
        },
      );
      if (response.status === 200) {
        showSuccessToast("Promise Date Updated Successfully");
        fetchProjectionDispatchData();
      }
    } catch (error) {
      showErrorToast(error?.response?.data?.message);
    }
  };

  const onRowEditCompleteProjection = async (e) => {
    const { newData } = e;
    const { remarks, OrderNum, target_date, revisedPendingAmt } = newData;

    const resolvedRevisedPendingAmt =
      revisedPendingAmt === undefined
        ? undefined
        : (revisedPendingAmt === "" ? null : revisedPendingAmt);

    // Optimistically update local state so table reflects the edited amount immediately
    setProjectionDispatchData((prev) =>
      prev.map((item) =>
        Number(item.OrderNum) === Number(OrderNum)
          ? {
            ...item,
            revisedPendingAmt:
              resolvedRevisedPendingAmt !== undefined
                ? resolvedRevisedPendingAmt
                : item.revisedPendingAmt,
            PendingAmt:
              revisedPendingAmt !== undefined
                ? revisedPendingAmt
                : item.PendingAmt,
          }
          : item,
      ),
    );

    handleRemarksUpdate(
      remarks,
      OrderNum,
      resolvedRevisedPendingAmt !== undefined
        ? resolvedRevisedPendingAmt
        : newData.revisedPendingAmt
    );
    if (target_date) {
      handleUpdateTargetDate(target_date, OrderNum);
    }
  };

  const remarksEditor = (options) => {
    return (
      <InputTextarea
        type="text"
        value={options?.value}
        onChange={(e) => options.editorCallback(e.target.value)}
        placeholder="Enter Remarks"
      />
    );
  };

  const remarksBodyTemplate = (rowData) => {
    return rowData?.remarks ? (
      <>
        <span id={rowData?.tooltipTarget} className="p-overlay-badge">
          {(rowData?.remarks?.length > 30
            ? rowData?.remarks.slice(0, 30) + "..."
            : rowData?.remarks) || "-"}
          {rowData?.remarks?.length > 30 ? (
            <FaCircleInfo className="ml-1" style={{ cursor: "pointer" }} />
          ) : null}
        </span>
        <Tooltip
          target={`#${rowData.tooltipTarget}`}
          // mouseTrack
          mouseTrackLeft={10}
          content={rowData?.remarks}
        />
      </>
    ) : (
      <span className="pl-2">-</span>
    );
  };

  const targetDateBodyTemplate = (rowData) => {
    const display = rowData.target_date
      ? new Date(rowData.target_date).toLocaleDateString("en-GB")
      : rowData.DueDt
      ? new Date(rowData.DueDt).toLocaleDateString("en-GB")
      : "-";

    const history =
      rowData.target_date_history ||
      null;

    const tooltipId = `promiseTooltip_${rowData.OrderNum || rowData.id || Math.random()}`;

    const historyString = Array.isArray(history)
      ? history
          .map((h) => (typeof h === "string" ? h : JSON.stringify(h)))
          .filter(Boolean)
          .join("\n")
      : history
      ? typeof history === "string"
        ? history.includes("|")
          ? history.split("|").map((s) => s.trim()).filter(Boolean).join("\n")
          : history
        : JSON.stringify(history)
      : "";

    return (
      <>
        <span id={tooltipId}>{display}</span>
        {historyString ? (
          <Tooltip target={`#${tooltipId}`} mouseTrack mouseTrackLeft={10} content={historyString} />
        ) : null}
      </>
    );
  };

  const targetDateEditor = (options) => {
    return (
      <Calendar
        value={options.value}
        onChange={(e) => options.editorCallback(e.value)}
        showIcon
        placeholder={
          options?.rowData?.target_date
            ? new Date(options?.rowData?.target_date).toLocaleDateString(
                "en-GB",
              )
            : "Select"
        }
        readOnlyInput
        dateFormat="dd/mm/yy"
      />
    );
  };

  const revisedPendingAmtEditor = (options) => {
    const value =
      options?.value ??
      options?.rowData?.revisedPendingAmt ??
      options?.rowData?.PendingAmt ??
      "";
    return (
      <InputText
        type="number"
        value={value}
        onChange={(e) => options.editorCallback(e.target.value)}
      />
    );
  };

  const determineRowClassNameProjection = (rowData) => {
    if (rowData.target_date && new Date(rowData.target_date) < new Date()) {
      return "promise_date_passed"; // Highlight rows with past target dates
    }
    return "";
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <div className="content" id="reports-page">
            <TabView
              activeIndex={activeTabIndex}
              onTabChange={(e) => setActiveTabIndex(e.index)}
            >
              <TabPanel header="Monthly Actual Dispatch">
                <div className="table-responsive ">
                  <DataTable
                    header={header}
                    value={getFilteredData()}
                    style={{ overflow: "scroll" }}
                    editMode="row"
                    paginator
                    rowsPerPageOptions={[20, 40, 60, 80, 100, 150]}
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                    rows={80}
                    size="small"
                    dataKey="id"
                    id="jobsTable"
                    ref={dt}
                    showGridlines
                    tableStyle={{ minWidth: "1400px" }}
                    emptyMessage="No records found"
                    // virtualScrollerOptions={{
                    //   itemSize: 48,
                    //   showLoader: true,
                    //   loading: isLoading,
                    // }}
                  >
                    <Column
                      field="ProdGroups"
                      header="Product Groups"
                      sortable={true}
                      body={renderProductCategory}
                    />
                    <Column
                      field="OrderNum"
                      header="Sales Order No."
                      sortable={true}
                      align={"right"}
                    />
                    <Column
                      field="OrderDt"
                      header="Order Date"
                      align="right"
                      sortable={true}
                      body={(rowData) =>
                        rowData?.OrderDt
                          ? new Date(rowData?.OrderDt).toLocaleDateString(
                              "en-GB",
                            )
                          : "-"
                      }
                    />
                    <Column
                      field="CustomerName"
                      header="Customer Name"
                      sortable={true}
                      body={(rowData) => {
                        const text = rowData?.CustomerName || "-";
                        const displayText =
                          text.length > 50
                            ? text.substring(0, 50) + "..."
                            : text;
                        return (
                          <>
                            <span
                              id={`customerNameTooltip_${rowData.OrderNum}`}
                            >
                              {displayText}
                            </span>
                            {text.length > 50 && (
                              <Tooltip
                                target={`#customerNameTooltip_${rowData.OrderNum}`}
                                content={text}
                              />
                            )}
                          </>
                        );
                      }}
                    />
                    <Column
                      field="State"
                      header="State"
                      sortable={true}
                      body={(rowData) => rowData?.State || "-"}
                    />
                    <Column
                      field="ProdQty"
                      header="Production Quantity"
                      align="right"
                      sortable={true}
                      body={(rowData) =>
                        rowData?.ProdQty ? rowData?.ProdQty : "-"
                      }
                    />

                    <Column
                      field="IndianAmt"
                      header={() =>
                        totalSumActual ? (
                          <span>
                            Total Amount
                            <br></br>
                            (Sum : ₹{" "}
                            {Number(totalSumActual).toLocaleString("en-IN")})
                          </span>
                        ) : (
                          "Total Amount"
                        )
                      }
                      body={(rowData) =>
                        rowData?.IndianAmt
                          ? Number(rowData?.IndianAmt).toLocaleString("en-IN")
                          : "-"
                      }
                      sortable={true}
                      align={"right"}
                    />
                    <Column
                      field="NeedDt"
                      header="Due Date"
                      sortable={true}
                      align="right"
                      body={(rowData) =>
                        rowData?.DueDt
                          ? new Date(rowData?.DueDt).toLocaleDateString("en-GB")
                          : "-"
                      }
                    />
                    <Column
                      field="release_date"
                      header="Release Date"
                      align="right"
                      sortable={true}
                      body={(rowData) =>
                        rowData?.release_date
                          ? new Date(rowData?.release_date).toLocaleDateString(
                              "en-GB",
                            )
                          : "-"
                      }
                    />
                  </DataTable>
                </div>
              </TabPanel>

              <TabPanel header="Monthly Projection Dispatch">
                <div className="table-responsive">
                  <DataTable
                    header={header}
                    value={getFilteredData()}
                    style={{ overflow: "scroll" }}
                    editMode="row"
                    rowClassName={determineRowClassNameProjection}
                    onRowEditComplete={onRowEditCompleteProjection}
                    paginator
                    first={projectionFirst}
                    onPage={(e) => {
                      setProjectionFirst(e.first);
                      setProjectionRows(e.rows);
                    }}
                    rowsPerPageOptions={[20, 40, 60, 80, 100, 150]}
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                    rows={projectionRows}
                    size="small"
                    dataKey="id"
                    id="projectionTable"
                    ref={dt}
                    showGridlines
                    emptyMessage={"No Data Found!"}
                    // virtualScrollerOptions={{
                    //   itemSize: 48,
                    //   showLoader: true,
                    //   loading: isLoading,
                    // }}
                  >
                    {/* <Column
                      field='ProdGroups'
                      header='Product Groups'
                      sortable={true}
                      body={renderProductCategory}
                    /> */}
                    <Column
                      field="OrderNum"
                      header="Sales Order"
                      sortable={true}
                      align={"right"}
                    />
                    <Column
                      field="OrderDt"
                      align={"right"}
                      header="Order Date"
                      sortable={true}
                      body={(rowData) =>
                        rowData?.OrderDt
                          ? new Date(rowData?.OrderDt).toLocaleDateString(
                              "en-GB",
                            )
                          : "-"
                      }
                    />
                    <Column
                      field="CustomerName"
                      header="Customer Name"
                      sortable={true}
                      body={(rowData) => {
                        const text = rowData?.CustomerName || "-";
                        const displayText =
                          text.length > 50
                            ? text.substring(0, 50) + "..."
                            : text;
                        return (
                          <>
                            <span
                              id={`customerNameTooltip_${rowData.OrderNum}`}
                            >
                              {displayText}
                            </span>
                            {text.length > 50 && (
                              <Tooltip
                                target={`#customerNameTooltip_${rowData.OrderNum}`}
                                content={text}
                              />
                            )}
                          </>
                        );
                      }}
                    />
                    {/* <Column
                      field='State'
                      header='State'
                      sortable={true}
                    /> */}
                    {/* <Column
                      field='OrderQty'
                      header='Order Quantity'
                      sortable={true}
                      body={(rowData) =>
                        rowData?.OrderQty ? rowData?.OrderQty : "-"
                      }
                      align={"right"}
                    /> */}
                    {/* <Column
                      field='IndianAmt'
                      header={() =>
                        totalSumProjectionOrder ? (
                          <span>
                            Order Amount<br></br> (Sum : ₹{" "}
                            {Number(totalSumProjectionOrder).toLocaleString(
                              "en-IN"
                            )}
                            )
                          </span>
                        ) : (
                          "Order Amount"
                        )
                      }
                      body={(rowData) =>
                        rowData?.IndianAmt
                          ? Number(rowData?.IndianAmt).toLocaleString("en-IN")
                          : "-"
                      }
                      sortable={true}
                      align={"right"}
                    /> */}
                    <Column
                      field="PendingQty"
                      header="Pending Quantity"
                      sortable={true}
                      body={(rowData) =>
                        rowData?.PendingQty ? rowData?.PendingQty : "-"
                      }
                      align={"right"}
                    />
                    <Column
                      field="PendingAmt"
                      header={() =>
                        totalSumProjectionPending ? (
                          <span>
                            Pending Amount<br></br> (Sum : ₹{" "}
                            {Number(totalSumProjectionPending).toLocaleString(
                              "en-IN",
                            )}
                            )
                          </span>
                        ) : (
                          "Pending Amount"
                        )
                      }
                      body={(rowData) =>
                        rowData?.PendingAmt
                          ? Number(rowData?.PendingAmt).toLocaleString("en-IN")
                          : "-"
                      }
                      sortable={true}
                      align={"right"}
                    />
                    <Column
                      field="revisedPendingAmt"
                      header={() =>
                        totalSumRevisedPending ? (
                          <span>
                            Revised Pending Amount<br></br> (Sum : ₹{" "}
                            {Number(totalSumRevisedPending).toLocaleString(
                              "en-IN",
                            )}
                            )
                          </span>
                        ) : (
                          "Revised Pending Amount"
                        )
                      }
                      editor={(options) => revisedPendingAmtEditor(options)}
                      body={(rowData) =>
                        rowData?.revisedPendingAmt !== undefined && rowData?.revisedPendingAmt !== null && rowData?.revisedPendingAmt !== ""
                          ? Number(rowData?.revisedPendingAmt).toLocaleString("en-IN")
                          : rowData?.PendingAmt
                          ? Number(rowData?.PendingAmt).toLocaleString("en-IN")
                          : "-"
                      }
                      align={"right"}
                    />
                    {/* <Column
                      field='OrderType'
                      header='Order Type'
                      sortable={true}
                      body={(rowData) =>
                        rowData?.OrderType ? rowData?.OrderType : "-"
                      }
                    /> */}
                    {/* <Column
                      field='SOCancel'
                      header='Line Cancel'
                      sortable={true}
                      body={(rowData) =>
                        rowData?.SOCancel ? rowData?.SOCancel : "-"
                      }
                    /> */}
                    <Column
                      field="DueDt"
                      header="Due Date"
                      align={"right"}
                      sortable={true}
                      body={(rowData) =>
                        rowData?.DueDt
                          ? new Date(rowData?.DueDt).toLocaleDateString("en-GB")
                          : "-"
                      }
                      />
            

                    <Column
                      field="target_date"
                      header="Promise Date"
                      align={"right"}
                      sortable={true}
                      editor={targetDateEditor}
                      body={targetDateBodyTemplate}
                    />
                    {/* <Column
                      field='release_date'
                      header='Release Date'
                      sortable={true}
                      body={(rowData) =>
                        rowData?.release_date
                          ? new Date(rowData?.release_date).toLocaleDateString(
                              "en-GB"
                            )
                          : "-"
                      }
                    /> */}
                      
                      <Column
                        field="LDClause"
                        header="LD Clause"
                        body={(rowData) => rowData?.LDClause || "-"}
                      />

                    <Column
                      field="remarks"
                      header="Remarks"
                      editor={(options) => remarksEditor(options)}
                      body={remarksBodyTemplate}
                      bodyStyle={{ paddingLeft: "20px" }}
                      headerStyle={{ minWidth: "250px", paddingLeft: "20px" }}
                    />

                    <Column
                      rowEditor={true}
                      headerStyle={{ textAlign: "left", minWidth: "80px" }}
                      header="Action"
                    />
                  </DataTable>
                </div>
              </TabPanel>

              <TabPanel header="Stage-wise Amount">
                <div className="table-responsive">
                  <DataTable
                    header={header}
                    value={getFilteredData()}
                    style={{ overflow: "scroll" }}
                    editMode="row"
                    paginator
                    rowsPerPageOptions={[20, 40, 60, 80, 100, 150]}
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                    rows={80}
                    size="small"
                    dataKey="id"
                    id="stagesTable"
                    ref={dt}
                    showGridlines
                    emptyMessage="No records found"
                  >
                    <Column
                      field="stage_name"
                      header="Stage Name"
                      sortable={true}
                    />
                    <Column
                      field="jobs_count"
                      header="Stage Load(No. of Jobs)"
                      sortable={true}
                      align={"right"}
                    />
                    <Column
                      field="total_IndianAmt"
                      header="Total Amount"
                      body={(rowData) =>
                        rowData?.total_IndianAmt
                          ? Math.round(rowData?.total_IndianAmt).toLocaleString(
                              "en-IN",
                            )
                          : "-"
                      }
                      sortable={true}
                      align={"right"}
                    />
                  </DataTable>
                </div>
              </TabPanel>
            </TabView>
          </div>
        </>
      )}
    </>
  );
};

export default Reports;
